import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Detects BLOCK-level markdown worth a full-width rendered card: a GFM pipe table
 * (header row + delimiter row) or a heading. This is a LAYOUT decision only — short
 * conversational answers stay ordinary bubbles (still markdown-rendered, so inline
 * emphasis displays properly); matches leave the bubble form so tables get room.
 */
export const containsBlockMarkdown = (text) => {
  const s = (text ?? '').toString();
  const hasTable = /(^|\n)\s*\|.+\|\s*\r?\n\s*\|[\s:|-]+\|/.test(s);
  const hasHeading = /(^|\n)#{1,6}\s+\S/.test(s);
  return hasTable || hasHeading;
};

// Markdown output inherits the chat's 12px scale; tables get compact GitHub-style
// borders so data answers read as data, not prose.
const components = {
  // A wide table (a stacking-height grid, six columns) in a narrow panel scrolls sideways
  // inside its own box rather than crushing its columns.
  table: ({ node, ...props }) => (
    <div style={{ overflowX: 'auto', margin: '6px 0' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%' }} {...props} />
    </div>
  ),
  th: ({ node, ...props }) => (
    <th style={{ border: '1px solid #d0d7de', padding: '4px 8px', background: '#f6f8fa', textAlign: 'left', fontWeight: 600 }} {...props} />
  ),
  td: ({ node, ...props }) => (
    <td style={{ border: '1px solid #d0d7de', padding: '4px 8px' }} {...props} />
  ),
  p: ({ node, ...props }) => <p style={{ margin: '4px 0' }} {...props} />,
  h1: ({ node, ...props }) => <div style={{ fontSize: 15, fontWeight: 700, margin: '6px 0 2px' }} {...props} />,
  h2: ({ node, ...props }) => <div style={{ fontSize: 14, fontWeight: 700, margin: '6px 0 2px' }} {...props} />,
  h3: ({ node, ...props }) => <div style={{ fontSize: 13, fontWeight: 700, margin: '6px 0 2px' }} {...props} />,
  ul: ({ node, ...props }) => <ul style={{ margin: '4px 0', paddingLeft: 18 }} {...props} />,
  ol: ({ node, ...props }) => <ol style={{ margin: '4px 0', paddingLeft: 18 }} {...props} />,
  a: ({ node, ...props }) => <a target="_blank" rel="noopener noreferrer" {...props} />,
};

// A link into the specification: the server rewrites the explainer's `[words](find:phrase)`
// into `[words](#find=<encoded phrase>)` — a fragment, which react-markdown's URL filter lets
// through. The phrase, decoded; null for any other link.
const FIND_FRAGMENT = '#find=';
export const findTermOf = (href) => {
  const h = (href ?? '').toString();
  if (!h.startsWith(FIND_FRAGMENT)) return null;
  try {
    return decodeURIComponent(h.slice(FIND_FRAGMENT.length)).replace(/\s+/g, ' ').trim() || null;
  } catch {
    return null;
  }
};

// The components with the links wired: a find link calls onFind(phrase) instead of opening
// a tab (a plain span when there is nothing to call); every other link opens in a new tab.
const componentsWithFind = (onFind) => ({
  ...components,
  a: ({ node, href, children, ...props }) => {
    const term = findTermOf(href);
    if (term == null) return <a target="_blank" rel="noopener noreferrer" href={href} {...props}>{children}</a>;
    if (!onFind) return <span>{children}</span>;
    return (
      <a
        href={href}
        className="spec-find-link"
        title={`Find in the specification: ${term}`}
        onClick={e => { e.preventDefault(); e.stopPropagation(); onFind(term); }}
      >
        {children}
      </a>
    );
  },
});

/**
 * Models use <br> for line breaks inside GFM table cells (cells can't hold real
 * newlines), and without raw-HTML rendering those tags show as literal text. This
 * remark plugin rewrites exactly that one pattern — an inline html node that is a
 * lone <br> — into a markdown hard break, which renders as a genuine line break
 * (including inside table cells) while raw HTML stays unrendered.
 */
const brToBreak = () => (tree) => {
  const walk = (node) => {
    if (!node.children) return;
    node.children = node.children.flatMap(child => {
      if (child.type === 'html') {
        const value = (child.value ?? '').trim();
        // A run of <br>s (e.g. "<br><br>") can arrive as one html node; emit one
        // break per tag.
        if (/^(<br\s*\/?>\s*)+$/i.test(value)) {
          const count = (value.match(/<br/gi) ?? []).length;
          return Array.from({ length: count }, () => ({ type: 'break' }));
        }
      }
      return [child];
    });
    node.children.forEach(walk);
  };
  walk(tree);
};

/**
 * Renders assistant markdown (GitHub-flavored: tables, lists, headings). react-markdown
 * never injects raw HTML, so model output needs no additional sanitizing.
 *
 * Memoized: ReactMarkdown re-parses the whole document on every render, which is
 * brutal for large texts (a build specification runs to hundreds of thousands of
 * characters) sitting in a dialog that re-renders per keystroke. Same text, same
 * output — so unchanged props skip the parse entirely.
 *
 * `onFind(phrase)`, when given, is what a `#find=` link does (see findTermOf); callers keep
 * it stable (useCallback) or every render re-parses.
 */
export const AccentMarkdown = React.memo(({ text, onFind }) => {
  const used = React.useMemo(() => (onFind ? componentsWithFind(onFind) : components), [onFind]);
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm, brToBreak]} components={used}>
      {(text ?? '').toString()}
    </ReactMarkdown>
  );
});
